import { useRef } from 'react';

import { useGlobalAccount } from '@/views/household/household-queries';
import { useSubmitTaskForm } from '@/views/tasks/hooks/useSubmitTaskForm';
import { TaskForm, TaskFormRef, TaskFormType } from '@/views/tasks/widgets/TaskForm';

import { useDockState } from '../dock-state';
import { CreateTaskDockItem as CreateTaskDockItemType } from '../dock-state-types';

// The dock item data is already in form shape; feed it back as form values.
// Round-tripping it through a TaskView would push the MM/DD/YYYY due date
// through the server-date parser, which mangles it (AD-1246).
export const toDefaultFormValues = (data: CreateTaskDockItemType['data']): Partial<TaskFormType> => ({
    title: data.title,
    description: data.description,
    householdOption: data.householdOption,
    assigneeOption: data.assigneeOption,
    taskTypeOption: data.taskTypeOption,
    taskStatusOption: data.taskStatusOption,
    dueDate: data.dueDate,
    isRecurring: data.isRecurring,
    frequencyOption: data.frequencyOption,
});

export const CreateTaskDockItem = (props: { item: CreateTaskDockItemType }) => {
    const { data: globalAccount } = useGlobalAccount(props.item.data.globalAccountGUID);
    const { setActiveId, updateDockItem, removeDockItem } = useDockState();
    const formRef = useRef<TaskFormRef>(null);
    const submitTaskForm = useSubmitTaskForm();

    if (props.item.data.globalAccountGUID && !globalAccount) return null;

    const defaultFormValues = toDefaultFormValues(props.item.data);

    return (
        <div
            onBlur={() => {
                const formValues = formRef.current?.formMethods.getValues();
                if (formValues) {
                    updateDockItem({
                        ...props.item,
                        data: {
                            ...props.item.data,
                            title: formValues?.title || '',
                            description: formValues?.description || '',
                            householdOption: formValues?.householdOption,
                            assigneeOption: formValues?.assigneeOption || { value: '', label: '' },
                            taskTypeOption: formValues?.taskTypeOption || { value: 'GENERAL', label: 'General' },
                            taskStatusOption: formValues?.taskStatusOption || { value: 'NEW', label: 'New' },
                            dueDate: formValues?.dueDate || '',
                            isRecurring: !!formValues?.isRecurring,
                            frequencyOption: formValues?.frequencyOption,
                            noteGUID: props.item.data.noteGUID,
                        },
                    });
                }
            }}
        >
            <TaskForm
                key={props.item.id}
                ref={formRef}
                householdMode={props.item.data.showHouseholdInput === false ? 'locked' : 'create'}
                initialNoHousehold={false}
                eventGUID={props.item.data.eventGUID}
                actionItemGUID={props.item.data.actionItemGUID}
                noteGUID={props.item.data.noteGUID}
                onSubmit={async (data) => {
                    if (await submitTaskForm.submit(data)) removeDockItem(props.item.id);
                }}
                defaultFormValues={defaultFormValues}
                onCancel={() => setActiveId(null)}
                householdGUID={props.item.data.globalAccountGUID}
                tenantGUID={globalAccount?.tenantGUID}
            />
        </div>
    );
};
